//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAuthHelper } from '../../AuthHelper';
import { useConfig } from '../../Config';
import { useLocation, useNavigate } from 'react-router-dom';
import LogoutConfirmation from './LogoutConfirmation';

function Logout() {
  const auth = useAuth();
  const { onLogout } = useAuthHelper();
  const { baseName } = useConfig();
  const redirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [cancelled, setCancelled] = useState(false);

  const params = new URLSearchParams(location.search);
  const reSignin = params.get('reSignin');

  // Imperative side-effects only — no setState here.
  useEffect(() => {
    if (redirected.current) return;

    /* 
    TODO We are re-signing in here to work around a Keycloak issues where the KC_RESTART cookie is lost
    //Re-signing in will re-establish the KC_RESTART token ( or direct the user to login if the session is invalid )
    */
    if (reSignin && auth.isAuthenticated) {
      redirected.current = true;
      auth.signinRedirect({
        redirect_uri: `${window.location.origin}${baseName}/logout`,
      });
    } else if (!auth.isLoading && !auth.isAuthenticated) {
      navigate('/');
    }
  }, [reSignin, auth, baseName, navigate, onLogout]);

  const handleConfirmLogout = useCallback(() => {
    onLogout();
    auth.signoutRedirect();
  }, [auth, onLogout]);

  const handleCancelLogout = useCallback(() => {
    setCancelled(true);
    navigate('/');
  }, [navigate]);

  // Show the Loop-side logout confirmation dialog when authenticated,
  // no reSignin flow in progress, and user hasn't cancelled.
  if (auth.isAuthenticated && !reSignin && !cancelled) {
    return (
      <LogoutConfirmation
        onLogout={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />
    );
  }

  return <div id="id-logout-empty" data-testid="logout-empty"></div>;
}

export default Logout;
